import './card.scss';
import CardToolbar, { CardToolbarProps } from './card-toolbar/card-toolbar';
import { useEffect, useRef, useState } from 'react';
import { FONT_COLOR, FONT_VARIANT } from '../../fonts/font-props';
import FontBody2 from '../../fonts/font-body2/font-body2';
import FontHeading5 from '../../fonts/font-heading5/font-heading5';
import { useResizeDetector } from 'react-resize-detector';
import LinearLoader from '../../loaders/linear-loader/linear-loader';
import useScrollComponent from '../../../hooks/scoll-hook/scroll-hook-component';
import { ITooltipProps, Tooltip } from '../../tooltip/tooltip';
import { CARD_SPACING_VARIANT } from './card.types';
import { isSet } from '../../../models/model-types-methods';

export interface ICardTooltipProps extends Omit<ITooltipProps, 'children'> {}

interface Props {
    toolbar?: CardToolbarProps,
    children: React.ReactNode,
    disabled?: boolean,
    disabledTooltip?: ICardTooltipProps,
    isExpanded?: boolean,
    isExpandable?: boolean,
    isExpandDisabled?: boolean,
    expandCallback?: (expanded: boolean) => void,
    note?: {
        title: string,
        description: string,
        isHtml?: boolean,
    },
    backgroundStyle?: Pick<React.CSSProperties, 'backgroundImage' | 'backgroundPosition' | 'backgroundSize'>,
    isLoading?: boolean,
    isScrollToViewAtMount?: boolean,
    spacingVariant?: CARD_SPACING_VARIANT,
    dataName?: string; /** used by Candu to target an element */
}

const Card: React.FC<Props> = (props) => {
    props = {
        ...props,
        disabled: typeof props.disabled === "undefined" ? false : props.disabled,
        isExpanded: typeof props.isExpanded === "undefined" ? true : props.isExpanded,
        isExpandable: typeof props.isExpandable === "undefined" ? true : props.isExpandable,
        isExpandDisabled: typeof props.isExpandDisabled === "undefined" ? false : props.isExpandDisabled,
        spacingVariant: typeof props.spacingVariant === "undefined" ? CARD_SPACING_VARIANT.DEFAULT : props.spacingVariant
    };

    const {
        toolbar, children, disabled, disabledTooltip, isExpanded, isExpandable, isExpandDisabled, expandCallback, note,
        backgroundStyle, isLoading, isScrollToViewAtMount, spacingVariant, dataName
    } = props;

    const expandContentRef = useRef<HTMLDivElement>(null);
    const [_isExpanded, setIsExpanded] = useState(isExpanded);
    const [height, setHeight] = useState({ height: isExpanded ? expandContentRef.current?.scrollHeight : '0' });
    const { elemRef, scrollIntoView } = useScrollComponent<HTMLDivElement>(isScrollToViewAtMount);

    useResizeDetector<HTMLDivElement>({
        targetRef: expandContentRef,
        onResize: ({ height }) => {
            if (expandContentRef.current && _isExpanded && isSet(height)) {
                setHeight({height});
            }
        },
    });

    useEffect(() => {
        setIsExpanded(isExpanded);
        setHeight({
            height: `${isExpanded ? expandContentRef.current?.scrollHeight : 0}px`
        });
    }, [isExpanded]);

    const toggleExpand = () => {
        if (disabled) {
            return;
        }

        if (!_isExpanded) {
            scrollIntoView();
        }

        setIsExpanded(!_isExpanded);
        if (expandCallback) {
            expandCallback(!_isExpanded);
        }
        setHeight({
            height: `${!_isExpanded ? expandContentRef.current?.scrollHeight : 0}px`
        });
    };


    const CardComponent = (
        <div ref={elemRef} data-name={dataName ?? ''} className={`card ${_isExpanded ? 'expanded' : ''} ${disabled ? 'disabled' : ''} ${spacingVariant}`}>
            {toolbar &&
                <CardToolbar spacingVariant={spacingVariant} {...toolbar} onExpandClick={toggleExpand} isExpanded={_isExpanded} isExpandable={isExpandable} isExpandDisabled={isExpandDisabled} />
            }
            <div className={`card-collapsable ${toolbar ? 'bordered' : ''}`} style={height}>
                {isLoading && <LinearLoader />}
                <div className={`card-content-container ${note ? 'with-note' : ''}`} ref={expandContentRef}>
                    <div className={`card-content ${_isExpanded ? 'expanded' : ''}`} style={backgroundStyle}>
                        {children}
                    </div>
                    {note &&
                        <div className={`card-note ${_isExpanded ? 'expanded' : ''}`}>
                            <>
                                <FontHeading5 color={FONT_COLOR.ACCENT1} variant={[FONT_VARIANT.UPPERCASE, FONT_VARIANT.BOLD]}>{note.title}</FontHeading5>
                                <FontBody2>{
                                    note?.isHtml ?
                                        <span
                                            dangerouslySetInnerHTML={{ __html: note.description }}
                                        /> :
                                        note.description
                                }
                                </FontBody2>
                            </>
                        </div>
                    }
                </div>
            </div>
        </div>
    );

    return (disabled && disabledTooltip)
        ? <Tooltip {...disabledTooltip}>{CardComponent}</Tooltip>
        : CardComponent;
}

export default Card;
